<script>
import VueDatePicker from "@vuepic/vue-datepicker";
import BaseTable from "@/javascript/views/components/charts/BaseTable.vue";
import CurrencyInput from "@/javascript/views/components/currencyInput/currencyInput.vue";
import DragNDrop from "@/javascript/views/clientDashboard/brand/dragNDrop.vue";
import Chip from "../inputs/Chip.vue";
import Icon from "../Icon.vue";
import BaseModal from "../BaseModal.vue";
import ImageCropper from "@/javascript/views/components/ImageCropper.vue";
import LoadingSpinner from "../loadingSpinner.vue";
import "vue-advanced-cropper/dist/style.css";
import Tooltip from "../tooltip.vue";

export default {
  name: "BaseFormTable",
  components: {
    Tooltip,
    Icon,
    Chip,
    VueDatePicker,
    BaseTable,
    CurrencyInput,
    DragNDrop,
    BaseModal,
    ImageCropper,
    LoadingSpinner
  },
  props: {
    /**
     * Whether the table is loading.
     */
    loading: {
      type: Boolean,
      default: true
    },
    /**
     * The record being manipulated.
     */
    record: {
      type: Object,
      required: true
    },
    /**
     * Array of objects representing rows, with each row being its own form field.
     * Each object should contain the following properties:
     * - 'label' = a string that will be used as the display label of the field
     * - 'key' = the key of the attribute in the record that is being modified
     * - 'input' = the type of the input which can be 'text', 'textarea', 'money', 'image', 'date', or 'select'
     *   - if 'select' input type, MUST also have an 'options' property with the select options being objects with
     *     a 'label' attribute representing the label string and a 'value' attribute representing the value
     *     that will be returned to the server
     */
    rows: {
      type: Array,
      required: true
    },
    errors: {
      type: Object,
      default: null
    },
    // props to pass to BaseTable
    /**
     * The message to show where there is no data available.
     */
    noDataMessage: {
      type: String,
      default: "Not enough data to report on."
    },
    /**
     * Number of static rows to show in the loading state
     */
    loadingRows: {
      type: Number,
      default: 5
    },
    /**
     * Number of static cells to show in the loading rows
     */
    loadingCells: {
      type: Number,
      default: 2
    },
    //TODO need to get access to organization timezone
    timezone: {
      type: String,
      default: "America/Detroit"
    }
  },
  emits: ["input", "remove-image", "cropped-file"],
  data() {
    return {
      arrayAddingField: "",
      tempEntryValue: "",
      showCropModal: false,
      croppingOriginalFile: null,
      croppingImageSrc: null,
      croppingKey: null,
      croppingRatio: null,
    };
  },
  created() {
    window.addEventListener("resize", this.onWindowResize);
  },
  unmounted() {
    window.removeEventListener("resize", this.onWindowResize);
  },
  methods: {
    onWindowResize() {
      if (this.$refs.datepicker) {
        this.$refs.datepicker.forEach(dp => dp.closeMenu());
      }
    },
    toggleArrayEntryField(key) {
      this.tempEntryValue = "";
      this.arrayAddingField = key;
    },
    addArrayEntry(arrayVal, entryType) {
      if (entryType === "number") {
        const n = parseFloat(this.tempEntryValue);
        if (isNaN(n)) return;
        arrayVal.push(n % 1 === 0 ? n.toString() : n.toFixed(2));
        arrayVal.sort((a, b) => a - b);
      } else {
        arrayVal.push(this.tempEntryValue);
      }
      this.tempEntryValue = "";
      this.arrayAddingField = "";
    },
    removeArrayEntry(arrayVal, index) {
      arrayVal.splice(index, 1);
    },
    getArrayDisplayValue(fn, v) {
      return typeof fn === "function" ? fn(v) : v;
    },
    removeImageLocally() {
      this.$emit("remove-image");
    },
    onFileSelected(file, key) {
      this.croppingOriginalFile = file;
      this.croppingKey = key;
      this.croppingRatio = key === 'banner_image' ? 16/9 : null;
      const reader = new FileReader();
      reader.onload = e => {
        this.croppingImageSrc = e.target.result;
        this.showCropModal = true;
      };
      reader.readAsDataURL(file);
    },
    onCropComplete(dataUrl) {
      const baseName = this.croppingOriginalFile.name.replace(/\.[^.]+$/, "");
      const [header, b64] = dataUrl.split(",");
      const mime = header.match(/:(.+?);/)[1];
      const bin = atob(b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const file = new File([arr], `${baseName}.png`, { type: mime });

      // Update preview
      this.$emit("input", `${this.croppingKey}_preview`, dataUrl);
      // Emit file for upload
      this.$emit("cropped-file", { key: this.croppingKey, file });

      // Reset
      this.showCropModal = false;
      this.croppingImageSrc = null;
      this.croppingOriginalFile = null;
      this.croppingKey = null;
    }
  }
};
</script>

<template>
  <base-table
    :loading="loading"
    :has-data="rows.length > 0"
    hide-header
    :no-data-message="noDataMessage"
    :loading-rows="loadingRows"
    :loading-cells="loadingCells"
  >
    <template #tableBody>
      <tr
        v-for="(row, i) in rows"
        :key="i"
      >
        <td class="w-1/4">
          <div>{{ row.label }}</div>
          <div
            v-if="row.helperText"
            class="caption-regular"
          >
            *{{ row.helperText }}
          </div>
        </td>
        <td class="input-td">
          <!-- Text Input -->
          <template v-if="row.input === 'text'">
            <div>
              <input
                :value="record[row.key]"
                type="text"
                :maxlength="row.maxlength"
                class="max-w-[300px] w-3/4 border rounded border-gray-300 px-2 py-1"
                @input="e => $emit('input', row.key, e.target.value)"
              >
              <div
                v-if="row.key === 'short_description'"
                class="text-xs text-gray-400 mt-1"
              >
                {{ (record[row.key] || '').length }} / 100
              </div>
              <!--              <div v-if="row.key === 'short_description' " class=" text-xs text-gray-500 mt-1">-->
              <!--                <tooltip-->
              <!--                  class="mt-3 ml-2"-->
              <!--                  message="A longer description for donors looking for more information on your current needs."-->
              <!--                  color="#3a4473"-->
              <!--                />-->
              <!--              </div>-->
            </div>
          </template>

          <!-- Textarea Input -->
          <template v-if="row.input === 'textarea'">
              <textarea
                :value="record[row.key]"
                :rows="row.rows || 5"
                class="w-full border rounded border-gray-300 px-2 py-1"
                @input="e => $emit('input', row.key, e.target.value)"
              />
          </template>

          <!-- Select Input -->
          <template v-if="row.input === 'select'">
            <select
              :value="record[row.key]"
              class="max-w-[300px] mt-1 block w-3/4 px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              @change="e => $emit('input', row.key, e.target.value)"
            >
              <option
                v-for="option in row.options"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </template>

          <!-- Read-only Input -->
          <template v-if="row.input === 'readonly'">
            <div class="max-w-[300px] w-3/4 px-2 py-1 text-gray-700">
              {{ row.data }}
            </div>
          </template>

          <!-- Date Input -->
          <template v-if="row.input === 'date'">
            <vue-date-picker
              ref="datepicker"
              :model-value="record[row.key]"
              :min-date="row.min_date ? record[row.min_date] : undefined"
              :enable-time-picker="!row.date_only"
              :model-type="row.date_only ? 'yyyy-MM-dd' : undefined"
              placeholder="Select"
              prevent-min-max-navigation
              auto-apply
              teleport
              class="max-w-[300px]"
              :timezone="row.date_only ? undefined : timezone"
              @update:model-value="val => {
                if (row.dependant && Date.parse(record[row.dependant]) > Date.parse(val)) {
                  $emit('input', row.dependant, null);
                }
                $emit('input', row.key, val);
              }"
            />
          </template>

          <!-- Money Input -->
          <template v-if="row.input === 'money'">
            <currency-input
              :model-value="record[row.key] / 100"
              prefix="$ "
              :max="20000000"
              class="max-w-[300px] mt-1 mb-2 block w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              @update:model-value="val => $emit('input', row.key, Math.floor(val * 100))"
            />
          </template>

          <!-- Chips Input -->
          <template v-if="row.input === 'chips'">
            <div class="grid grid-cols-[repeat(auto-fill,_minmax(100px,_1fr))] gap-3 w-full">
              <div
                v-for="(val, idx) in record[row.key]"
                :key="idx"
              >
                <chip
                  size="large"
                  type="dismissible"
                  @dismiss="removeArrayEntry(record[row.key], idx)"
                >
                  <div class="text-md font-semibold">
                    {{ getArrayDisplayValue(row.displayValue, val) }}
                  </div>
                </chip>
              </div>
              <div
                v-if="arrayAddingField === row.key"
                class="flex-row inline-flex col-span-2"
              >
                <input
                  v-model="tempEntryValue"
                  type="text"
                  class="max-w-[100px] border rounded border-gray-300 px-2 py-2"
                >
                <div
                  class="add-array-entry-button ml-1"
                  @click="addArrayEntry(record[row.key], row.inputType)"
                >
                  <icon
                    icon="check"
                    size="16px"
                    color="white"
                  />
                </div>
              </div>
              <div
                v-else
                class="add-array-entry-button"
                @click="toggleArrayEntryField(row.key)"
              >
                <icon
                  icon="plus"
                  size="14px"
                  color="white"
                />
              </div>
            </div>
          </template>

          <!-- Checkbox Input -->
          <template v-if="row.input === 'checkbox'">
            <input
              v-model="record[row.key]"
              type="checkbox"
              @change="e => $emit('input', row.key, e.target.checked)"
            >
          </template>

          <!-- Image Input -->
          <template v-if="row.input === 'image'">
            <div class="flex items-center space-x-2">
              <template v-if="record[row.key + '_preview']">
                <div class="image-preview-container flex items-center">
                  <img
                    :src="record[row.key + '_preview']"
                    alt="Image Preview"
                    class="max-h-[200px] max-w-[200px] rounded"
                  >
                  <button
                    class="remove-image-btn ml-2"
                    title="Remove Image"
                    @click.stop="removeImageLocally"
                  >
                    <Icon
                      icon="trash"
                      class="icon-trash"
                      :size="'18px'"
                      :color="'#ff4d4d'"
                    />
                  </button>
                </div>
              </template>
              <template v-else-if="record[row.key]">
                <div class="image-preview-container flex items-center">
                  <img
                    :src="record[row.key]"
                    alt="Existing Image"
                    class="max-h-[200px] max-w-[200px] rounded"
                  >
                  <button
                    class="remove-image-btn ml-2"
                    title="Remove Image"
                    @click.stop="removeImageLocally"
                  >
                    <Icon
                      icon="trash"
                      class="icon-trash"
                      :size="'18px'"
                      :color="'#ff4d4d'"
                    />
                  </button>
                </div>
              </template>
              <template v-else>
                <drag-n-drop
                  compact
                  validate
                  file-type="image"
                  class="max-w-[300px] w-3/4 border rounded border-gray-300 px-2 py-1"
                  @file-selected="file => onFileSelected(file, row.key)"
                />
              </template>
            </div>
          </template>

          <!-- Error Message -->
          <p
            v-if="errors && row.key in errors"
            class="text-default_error mt-1"
          >
            {{ errors[row.key] }}
          </p>
        </td>
      </tr>
    </template>
  </base-table>

  <!-- Cropping Modal -->
  <base-modal
    v-if="showCropModal"
    title="Crop Image"
    size="medium"
    @close="showCropModal = false"
  >
    <div v-if="croppingImageSrc">
      <image-cropper
        :src="croppingImageSrc"
        :aspect-ratio="croppingRatio"
        @update="onCropComplete"
        @cancel="() => { showCropModal = false; croppingImageSrc = null; croppingRatio = null; }"
      />
    </div>
    <div
      v-else
      class="flex justify-center items-center p-4"
    >
      <loading-spinner size="icon-large" />
    </div>
  </base-modal>
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.input-td {
  max-width: 300px;
}

.add-array-entry-button {
  @apply bg-blue-600 rounded-full flex flex-col justify-center items-center cursor-pointer;
  width: 32px;
  height: 32px;
  &:hover {
    @apply bg-default_secondary;
  }
}

.image-preview-container {
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: white;
  gap: 8px;
}

.remove-image-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease;
}
.remove-image-btn:hover {
  transform: scale(1.1);
}
</style>
